
// FontAwesome
import { faEye, faPlus } from '@fortawesome/free-solid-svg-icons';
import { faChevronUp } from '@fortawesome/free-solid-svg-icons/faChevronUp';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// Components
import { FhirStatus, Proof, StatusTag, Thread, Title } from '@fyrstain/fhir-front-library';
import AddTestOperation from '../AddTestOperation/AddTestOperation';
import TestMessageEdition from '../TestMessageEdition/TestMessageEdition';
import TestOperationStatusSelector from '../TestOperationStatusSelector/TestOperationStatusSelector';
// FHIR
import { TestReport, TestReportSetupAction, TestReportTeardownAction, TestReportTestAction, TestScript } from 'fhir/r5';
// Translation
import i18n from 'i18next';
// React
import { useReducer, useState } from 'react';
// React Bootstrap
import { Accordion, Card } from 'react-bootstrap';
// Authentication
import UserService from 'src/services/UserService';
// Styles
import styles from './TestOperationSection.module.css';

////////////////////////
//      Props         //
////////////////////////

interface TestOperationSectionProps {
    // The operations to display
    operations: any;
    // The asserts to display
    asserts?: any;
    // The TestReport resource 
    testReport: TestReport;
    // The TestScript resource 
    testScript: TestScript;
    // The test step operation to display in the section header
    testStepHeaderOperation: string;
    // The test step of the operations
    testStepOperation: any;
    // The test step of the asserts
    testStepAssert?: any;
    // If the test is a manual test
    isManualTest: boolean;
    // The title of the section
    titleSection: string;
    // If the section is a teardown section
    isTeardown?: boolean;
    // The add operation function
    addTestOperation: any;
    // The add assert function
    addTestAssert?: any;
    // The id of the test
    testId?: string;
    // The function to get the detail of an operation or an assert
    onAddOperation?: (operation: any) => void;
    // The function to get the detail of an assert
    onAddAssert?: (assert: any) => void;
    // Function to call when an error occurs
    onError: () => void;
    // Async function to get the detail of an operation or an assert
    onDetail: (id: string | undefined) => Promise<string>;
    // To show add icon if it's not a test section
    showAddFaPlusIcon?: boolean;
    // Callback to get the latest changes made in the testReport
    onUpdateTestReport?: (updatedTestReport: TestReport) => void;
    // The operations that already exist in the testReport
    existingOperationIds?: { setup: string[]; test: string[]; teardown: string[] };
    // Function to set the isCreatingTest state in the parent component
    setIsCreatingTest?: (isCreating: boolean) => void;
    // Function to call when the refresh button is clicked
    refreshTestReport: (testReportId: string) => Promise<TestReport>;
}

const TestOperationSection: React.FC<TestOperationSectionProps> = (configs) => {

    /////////////////////////////
    //          States         //
    /////////////////////////////

    const [ignored, forceUpdate] = useReducer(x => x + 1, 0);
    const [addHeaderAndRowFn, setAddHeaderAndRowFn] = useState<() => void>(() => { });

    /////////////////////////////
    //         Constant        //
    /////////////////////////////

    const reactAppFhirUrl = process.env.REACT_APP_FHIR_URL ?? 'https://integ.fyrstain.com/r5-data';

    /////////////////////////////
    //        Functions        //
    /////////////////////////////

    /**
     * Handle the header and row
     * 
     */
    const handleAddHeaderAndRow = () => {
        if (addHeaderAndRowFn) {
            addHeaderAndRowFn();
        }
    };

    /**
     * Handle the addition of an operation.
     * 
     * @param operation 
     */
    const handleAddOperation = (operation: any) => {
        if (configs.onAddOperation) {
            configs.onAddOperation(operation);
        }
    };

    /**
     * Handle the addition of an assert.
     * 
     * @param assert
     */
    const handleAddAssert = (assert: any) => {
        if (configs.onAddAssert) {
            configs.onAddAssert(assert);
        }
    };

    /**
    * Determine the overall status for a set of operations.
    * 
    * @param operations The operations to check.
    * @returns The overall status.
    */
    function prioritizeStatus(operations: (TestReportSetupAction | TestReportTestAction | TestReportTeardownAction)[]): string {
        let status = 'pass';
        let hasSkip = false;
        for (const operation of operations) {
            if (!operation) continue;
            switch (operation.operation?.result) {
                case 'fail':
                    return 'fail';
                case 'error':
                    if (status !== 'fail') {
                        status = 'error';
                    }
                    break;
                case 'warning':
                    if (status !== 'fail' && status !== 'error') {
                        status = 'warning';
                    }
                    break;
                case 'skip':
                    hasSkip = true;
                    break;
                default:
                    break;
            }
        }
        if (status === 'pass' && hasSkip) {
            return 'pass';
        }
        return status;
    }

    /////////////////////////////
    //         Content         //
    /////////////////////////////

    return (
        <div className={configs.addTestOperation !== 'testOperation' ? "section" : styles.testSection}>
            {configs.isManualTest && (
                <Accordion
                    defaultActiveKey={
                        configs.operations.length === 0
                            ? undefined
                            : configs.operations.every((operation: any) => operation.operation.operation?.result === 'pass')
                                ? undefined
                                : "0"
                    }
                >
                    <Accordion.Item eventKey="0">
                        <Accordion.Header >
                            <div className={["spaceBetweenContainer", styles.accordionHeaderContainer].join(' ')}>
                                <div className="displayFlexCenter">
                                    {configs.operations.length > 0 && (
                                        <StatusTag
                                            status={FhirStatus[prioritizeStatus(configs.operations.map((operation: any) => operation.operation)) as keyof typeof FhirStatus]}
                                            statusMessage={prioritizeStatus(configs.operations.map((operation: any) => operation.operation))}
                                        />
                                    )}
                                    <div className={styles.titleContainer}>
                                        <Title
                                            level={configs.titleSection !== configs.testId ? 2 : 3}
                                            content={configs.titleSection}
                                        />
                                    </div>
                                    {configs.operations.length === 0 && configs.showAddFaPlusIcon && (
                                        <FontAwesomeIcon
                                            className={["actionIcon", styles.addOperationIcon].join(' ')}
                                            icon={faPlus}
                                            title={`${i18n.t('general.addoperation')} ${configs.testStepHeaderOperation}`}
                                            onClick={handleAddHeaderAndRow}
                                        />
                                    )}
                                </div>
                                {configs.operations.length > 0 && (
                                    <div className="displayFlexCenter">
                                        <Proof
                                            serverUrl={reactAppFhirUrl}
                                            userIdentifier={UserService.getUsername()}
                                            testReport={configs.testReport}
                                            testScript={configs.testScript}
                                            testStep={configs.testStepHeaderOperation}
                                            language={i18n.t}
                                            onError={configs.onError}
                                            refreshTestReport={configs.refreshTestReport}
                                        />
                                        <Thread
                                            serverUrl={reactAppFhirUrl}
                                            userIdentifier={UserService.getUsername()}
                                            onError={configs.onError}
                                            testReport={configs.testReport}
                                            testScript={configs.testScript}
                                            testStep={configs.testStepHeaderOperation}
                                            language={i18n.t}
                                            refreshTestReport={configs.refreshTestReport}
                                        />
                                    </div>
                                )}
                            </div>
                        </Accordion.Header>
                        {configs.operations.length > 0 &&
                            <Accordion.Body>
                                <div className="container col-md-12 panel panel-default panel-body">
                                    <table className="table table-condensed table-striped">
                                        <thead>
                                            <tr>
                                                <th>
                                                    {i18n.t('label.name')}
                                                </th>
                                                <th>
                                                    {i18n.t('label.result')}
                                                </th>
                                                <th>
                                                    Messages
                                                </th>
                                                <th>
                                                    Actions
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {configs.operations.map((operation: any) => (
                                                <>
                                                    <tr className={styles.operationRow}>
                                                        <td className={styles.columnsWidthOperation}>
                                                            {configs.testStepOperation(operation)}
                                                        </td>
                                                        <td className={styles.smallColumnsWidthOperation}>
                                                            <TestOperationStatusSelector
                                                                testReport={configs.testReport}
                                                                testStep={configs.testStepOperation(operation)}
                                                                updateType="result"
                                                            />
                                                        </td>
                                                        <td className={styles.largeColumnsWidthOperation}>
                                                            <TestMessageEdition
                                                                testReport={configs.testReport}
                                                                testStep={configs.testStepOperation(operation)}
                                                            />
                                                        </td>
                                                        <td className={styles.smallColumnsWidthOperation}>
                                                            <div className="flexWrapStart">
                                                                {configs.operations.length > 0 && (
                                                                    <div className="displayFlexCenter">
                                                                        <Proof
                                                                            serverUrl={reactAppFhirUrl}
                                                                            userIdentifier={UserService.getUsername()}
                                                                            onError={configs.onError}
                                                                            testReport={configs.testReport}
                                                                            testScript={configs.testScript}
                                                                            testStep={configs.testStepOperation(operation)}
                                                                            language={i18n.t}
                                                                            refreshTestReport={configs.refreshTestReport}
                                                                        />
                                                                        <Thread
                                                                            serverUrl={reactAppFhirUrl}
                                                                            userIdentifier={UserService.getUsername()}
                                                                            onError={() => { }}
                                                                            testReport={configs.testReport}
                                                                            testScript={configs.testScript}
                                                                            testStep={configs.testStepOperation(operation)}
                                                                            language={i18n.t}
                                                                            refreshTestReport={configs.refreshTestReport}
                                                                        />
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            {!configs.isTeardown && !operation.toggle && (
                                                                <FontAwesomeIcon
                                                                    className={styles.chevron}
                                                                    icon={faChevronUp}
                                                                    rotation={180}
                                                                    onClick={() => {
                                                                        operation.toggle = !operation.toggle;
                                                                        forceUpdate();
                                                                    }}
                                                                />
                                                            )}
                                                            {!configs.isTeardown && operation.toggle && (
                                                                <FontAwesomeIcon
                                                                    className={styles.chevron}
                                                                    icon={faChevronUp}
                                                                    onClick={() => {
                                                                        operation.toggle = !operation.toggle;
                                                                        forceUpdate();
                                                                    }}
                                                                />
                                                            )}
                                                        </td>
                                                    </tr >
                                                    <tr>
                                                        <td colSpan={12}>
                                                            {!configs.isTeardown && operation.toggle && (
                                                                <>
                                                                    <table className="table table-striped">
                                                                        <tbody>
                                                                            {operation.asserts && operation.asserts.map((assert: any, assertIndex: number) => (
                                                                                <tr key={assert.assert?.id || assertIndex}>
                                                                                    <td className={styles.columnsWidthAssert}>
                                                                                        {assert.assert?.id ?? 'N/A'}
                                                                                    </td>
                                                                                    <td className={styles.smallColumnsWidthAssert}>
                                                                                        <TestOperationStatusSelector
                                                                                            testReport={configs.testReport}
                                                                                            testStep={configs.testStepAssert(assert)}
                                                                                            updateType="result"
                                                                                        />
                                                                                    </td>
                                                                                    <td className={styles.largeColumnsWidthAssert}>
                                                                                        <TestMessageEdition
                                                                                            testReport={configs.testReport}
                                                                                            testStep={assert.assert?.id ?? 'N/A'}
                                                                                        />
                                                                                    </td>
                                                                                    <td>
                                                                                        <div className="flexWrapStart">
                                                                                            {configs.operations.length > 0 && (
                                                                                                <div className="displayFlexCenter">
                                                                                                    <Proof
                                                                                                        serverUrl={reactAppFhirUrl}
                                                                                                        userIdentifier={UserService.getUsername()}
                                                                                                        onError={configs.onError}
                                                                                                        testReport={configs.testReport}
                                                                                                        testScript={configs.testScript}
                                                                                                        testStep={configs.testStepAssert(assert)}
                                                                                                        language={i18n.t}
                                                                                                        refreshTestReport={configs.refreshTestReport}
                                                                                                    />
                                                                                                    <Thread
                                                                                                        serverUrl={reactAppFhirUrl}
                                                                                                        userIdentifier={UserService.getUsername()}
                                                                                                        onError={configs.onError}
                                                                                                        testReport={configs.testReport}
                                                                                                        testScript={configs.testScript}
                                                                                                        testStep={configs.testStepAssert(assert)}
                                                                                                        language={i18n.t}
                                                                                                        refreshTestReport={configs.refreshTestReport}
                                                                                                    />
                                                                                                </div>
                                                                                            )}
                                                                                        </div>
                                                                                    </td>
                                                                                </tr>
                                                                            ))}
                                                                        </tbody>
                                                                    </table>
                                                                    <AddTestOperation
                                                                        operationType='assert'
                                                                        testReport={configs.testReport}
                                                                        testStep={configs.addTestAssert}
                                                                        onAddAssert={handleAddAssert}
                                                                        testId={configs.testId}
                                                                        hasOperations={configs.operations.length > 0}
                                                                        operationId={operation.operation.operation?.id}
                                                                        onUpdateTestReport={configs.onUpdateTestReport}
                                                                    />
                                                                </>
                                                            )}
                                                        </td>
                                                    </tr>
                                                </>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <AddTestOperation
                                    operationType='operation'
                                    testReport={configs.testReport}
                                    testStep={configs.addTestOperation}
                                    onAddOperation={handleAddOperation}
                                    hasOperations={configs.operations.length > 0}
                                    testId={configs.testId}
                                    onUpdateTestReport={configs.onUpdateTestReport}
                                    existingOperationIds={configs.existingOperationIds}
                                    setIsCreatingTest={configs.setIsCreatingTest}
                                />
                            </Accordion.Body>
                        }
                        {configs.operations.length === 0 &&
                            <Accordion.Body>
                                <AddTestOperation
                                    operationType='operation'
                                    testReport={configs.testReport}
                                    testStep={configs.addTestOperation}
                                    onAddOperation={handleAddOperation}
                                    hasOperations={configs.operations.length > 0}
                                    setAddHeaderAndRow={setAddHeaderAndRowFn}
                                    testId={configs.testId}
                                    onUpdateTestReport={configs.onUpdateTestReport}
                                    existingOperationIds={configs.existingOperationIds}
                                    setIsCreatingTest={configs.setIsCreatingTest}
                                />
                            </Accordion.Body>
                        }
                    </Accordion.Item>
                </Accordion>
            )}
            {!configs.isManualTest && configs.operations.length > 0 ? (
                <Accordion defaultActiveKey={configs.operations.every((operation: any) => operation.operation.operation?.result === 'pass') ? undefined : "0"}>
                    <Accordion.Item eventKey="0">
                        <Accordion.Header>
                            <div className={["spaceBetweenContainer", styles.accordionHeaderContainer].join(' ')}>
                                <div className="displayFlexCenter">
                                    <StatusTag
                                        status={FhirStatus[prioritizeStatus(configs.operations.map((operation: any) => operation.operation)) as keyof typeof FhirStatus]}
                                        statusMessage={prioritizeStatus(configs.operations.map((operation: any) => operation.operation))}
                                    />
                                    <div className={styles.titleContainer}>
                                        <Title
                                            level={configs.titleSection !== configs.testId ? 2 : 3}
                                            content={configs.titleSection}
                                        />
                                    </div>
                                </div>
                            </div>
                        </Accordion.Header>
                        <Accordion.Body>
                            <div className="container col-md-12 panel panel-default panel-body">
                                <table className="table table-condensed table-striped">
                                    <thead>
                                        <tr>
                                            <th>
                                                {i18n.t('label.name')}
                                            </th>
                                            <th>
                                                {i18n.t('label.result')}
                                            </th>
                                            <th>
                                                Messages
                                            </th>
                                            <th>
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {configs.operations.map((operation: any) => (
                                            <>
                                                <tr className={styles.operationRow}>
                                                    <td className={styles.columnsWidthOperation}>
                                                        {configs.testStepOperation(operation)}
                                                    </td>
                                                    <td className={styles.smallColumnsWidthOperation}>
                                                        <StatusTag
                                                            status={FhirStatus[operation.operation.operation?.result as keyof typeof FhirStatus]}
                                                            statusMessage={operation.operation.operation?.result ?? 'N/A'}
                                                        />
                                                    </td>
                                                    <td className={styles.largeColumnsWidthOperation}>
                                                        {operation.operation.operation?.message ?? 'N/A'}
                                                    </td>
                                                    <td className={styles.smallColumnsWidthOperation}>
                                                        <a
                                                            className="actionIcon"
                                                            onClick={async () => {
                                                                if (configs.onDetail && operation.operation.operation?.detail) {
                                                                    const url = await configs.onDetail(operation.operation.operation?.detail);
                                                                    window.open(url, "_blank", "noopener,noreferrer");
                                                                }
                                                            }}
                                                        >
                                                            <FontAwesomeIcon
                                                                className="actionIcon"
                                                                icon={faEye}
                                                            />
                                                        </a>
                                                    </td>
                                                    <td>
                                                        {!configs.isTeardown && !operation.toggle && (
                                                            <FontAwesomeIcon
                                                                className={styles.chevron}
                                                                icon={faChevronUp}
                                                                rotation={180}
                                                                onClick={() => {
                                                                    operation.toggle = !operation.toggle;
                                                                    forceUpdate();
                                                                }}
                                                            />
                                                        )}
                                                        {!configs.isTeardown && operation.toggle && (
                                                            <FontAwesomeIcon
                                                                className={styles.chevron}
                                                                icon={faChevronUp}
                                                                onClick={() => {
                                                                    operation.toggle = !operation.toggle;
                                                                    forceUpdate();
                                                                }}
                                                            />
                                                        )}
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td colSpan={12}>
                                                        {operation.toggle && !configs.isTeardown && (
                                                            <table className="table table-striped">
                                                                <thead>
                                                                </thead>
                                                                <tbody>
                                                                    {operation.asserts.map((assert: any) => <tr>
                                                                        <td className={styles.columnsWidthAssert}>
                                                                            {assert.assert?.id ?? 'N/A'}
                                                                        </td>
                                                                        <td className={styles.smallColumnsWidthAssert}>
                                                                            <StatusTag
                                                                                status={FhirStatus[assert.assert?.result as keyof typeof FhirStatus]}
                                                                                statusMessage={assert.assert?.result ?? 'N/A'}
                                                                            />
                                                                        </td>
                                                                        <td className={styles.largeColumnsWidthAssert}>
                                                                            {assert.assert?.message ?? 'N/A'}
                                                                        </td>
                                                                        <td>
                                                                            <div className="flexWrapStart">
                                                                                <a
                                                                                    className="actionIcon"
                                                                                    onClick={async () => {
                                                                                        if (configs.onDetail && assert.assert?.detail) {
                                                                                            const url = await configs.onDetail(assert.assert.detail);
                                                                                            window.open(url, "_blank", "noopener,noreferrer");
                                                                                        }
                                                                                    }}
                                                                                >
                                                                                    <FontAwesomeIcon
                                                                                        className="actionIcon"
                                                                                        icon={faEye}
                                                                                    />
                                                                                </a>
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                    )}
                                                                </tbody>
                                                            </table>
                                                        )}
                                                    </td>
                                                </tr>
                                            </>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>
            ) : (!configs.isManualTest && configs.operations.length === 0) && (
                <Card>
                    <Card.Header className={styles.noOperationCard}>
                        <Title
                            level={2}
                            content={configs.titleSection}
                        />
                        <b className={styles.infoMessage}>({i18n.t('infomessage.nooperation')})</b>
                    </Card.Header>
                </Card>
            )
            }
        </div >
    );
};

export default TestOperationSection;